import {Box, HBox} from '@buncha/components/core/view';
import {H3Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BarcodeScanIcon from '@buncha/components/core/icon/BarcodeScanIcon';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {CustomFlatList} from '@buncha/components/core/list';
import {CustomImage} from '@buncha/components/core/Image';
import {Divider} from 'native-base';
import {PButton} from '@buncha/components/core/Button';
import {StoreBarcodeList} from '@buncha/driver/types/printScanBarcode';
import {styles} from './style';
import {HomeScreens} from '@buncha/home/navigation/types';
import {Modules} from '@buncha/utils/types';
import {useGetLocale} from '@buncha/localization/hooks';

interface BarcodeScanReminderSheetProps {
  open: boolean;
  onClose: () => void;
  storeBarcodeList?: StoreBarcodeList[];
  onCTAPress: () => void;
}

const BarcodeScanReminderSheet: React.FC<
  BarcodeScanReminderSheetProps
> = props => {
  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');
  const muli = useFontTheme('muli');
  const storeBarcodeList = props?.storeBarcodeList ?? [];
  const {title, body, button} =
    useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const separator = useCallback(() => {
    return <Divider style={[styles.divider]} />;
  }, []);

  const closeSheetAndNavigate = useCallback(() => {
    props?.onClose();
    props?.onCTAPress();
  }, [props]);

  const renderItem = useCallback((item: {item: StoreBarcodeList}) => {
    const storeOrder = item?.item;
    return (
      <HBox>
        <HBox style={styles.storeBox}>
          <H6Text
            style={[
              {
                color: theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {storeOrder?.index}.
          </H6Text>
          <CustomImage
            style={[
              styles.image,
              {borderColor: theme?.background?.gray?.[200]},
            ]}
            source={storeOrder?.storeLogo}
            resizeMode="contain"
          />
          <H6Text
            style={[
              {
                color: theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {storeOrder?.storeName}
          </H6Text>
        </HBox>
        <HBox
          style={[
            styles.barcodeCountBox,
            {backgroundColor: theme?.background?.yellow?.[100]},
          ]}>
          <H6Text
            style={[
              {
                color: theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {storeOrder?.barcodeCount}
          </H6Text>
          <BarcodeScanIcon
            height={14}
            width={14}
            viewBox="0 0 14 14"
            fill={theme?.icon?.blue?.[300]}
          />
        </HBox>
      </HBox>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BottomSheet open={props?.open} onClose={props?.onClose}>
      <Box style={styles.parentBox}>
        <H3Text
          style={[
            styles.align,
            {
              color: theme?.text?.blue?.[300],
              fontFamily: mortise?.[700]?.normal,
            },
          ]}>
          {title?.scanningProcess}
        </H3Text>
        <H6Text
          style={[
            styles.align,
            {color: theme?.text?.gray?.[100], fontFamily: muli?.[700]?.normal},
          ]}>
          {body?.followOrder}
        </H6Text>
        <CustomFlatList
          ItemSeparatorComponent={separator}
          style={styles.flatList}
          data={storeBarcodeList}
          renderItem={renderItem}
        />
        <PButton
          onPress={closeSheetAndNavigate}
          style={[styles.cta]}
          title={button?.startScanning}
        />
      </Box>
    </BottomSheet>
  );
};

export default React.memo(BarcodeScanReminderSheet);
